import { useState } from 'react';
import { ConfirmDialog, Modal } from '../Modal';
import { ApiError } from '../../lib/api';
import { IconTrash } from '../icons';
import {
  useCharacterGoals,
  useCreateGoal,
  useDeleteGoal,
  useReorderGoals,
  useToggleGoalDone,
  type GoalDto,
} from '../../lib/characterProgress';
import { CollapsibleSection } from './CollapsibleSection';

const GOAL_TYPES = [
  { value: 'ITEM', label: 'Item (pitched / eternal piece)' },
  { value: 'FREE_TEXT', label: 'Free-text goal' },
];

function CreateGoalModal({ characterId, onClose }: { characterId: number; onClose: () => void }) {
  const createGoal = useCreateGoal(characterId);
  const [type, setType] = useState(GOAL_TYPES[0]?.value ?? 'ITEM');
  const [title, setTitle] = useState('');
  const [error, setError] = useState<string | null>(null);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    setError(null);
    if (!title.trim()) {
      setError('Title is required.');
      return;
    }
    createGoal.mutate(
      { type, title: title.trim() },
      {
        onSuccess: onClose,
        onError: (err) => setError(err instanceof ApiError ? err.serverMessage : 'Could not save this goal.'),
      },
    );
  };

  return (
    <Modal title="Add goal" onClose={onClose}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label htmlFor="goal-type" className="mb-1 block text-sm font-medium text-text-muted">
            Type
          </label>
          <select
            id="goal-type"
            value={type}
            onChange={(e) => setType(e.target.value)}
            className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text focus:border-accent focus:outline-none"
          >
            {GOAL_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label htmlFor="goal-title" className="mb-1 block text-sm font-medium text-text-muted">
            Title
          </label>
          <input
            id="goal-title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            maxLength={200}
            placeholder="e.g. Sweetwater Boots (Eternal)"
            className="min-h-tap w-full rounded-lg border border-border-strong bg-bg-elevated-hover px-3 text-sm text-text placeholder:text-text-faint focus:border-accent focus:outline-none"
          />
        </div>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="min-h-tap rounded-lg px-4 text-sm font-semibold text-text-muted hover:bg-bg-elevated-hover"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createGoal.isPending}
            className="min-h-tap rounded-lg bg-accent px-4 text-sm font-semibold text-accent-contrast shadow-sm transition-colors hover:bg-accent-strong disabled:opacity-50"
          >
            {createGoal.isPending ? 'Saving…' : 'Add goal'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

function GoalRow({
  characterId,
  goal,
  index,
  count,
  onReorder,
}: {
  characterId: number;
  goal: GoalDto;
  index: number;
  count: number;
  onReorder: (from: number, to: number) => void;
}) {
  const toggleDone = useToggleGoalDone(characterId);
  const deleteGoal = useDeleteGoal(characterId);
  const [confirmingDelete, setConfirmingDelete] = useState(false);

  return (
    <>
      <li className="flex min-h-tap items-center gap-3 rounded-lg border border-border bg-bg-elevated-hover px-3 py-2">
        <div className="flex shrink-0 flex-col">
          <button
            type="button"
            aria-label={`Move ${goal.title} up`}
            disabled={index === 0}
            onClick={() => onReorder(index, index - 1)}
            className="flex min-h-tap min-w-tap items-center justify-center text-xs text-text-faint hover:text-accent disabled:opacity-20"
          >
            ▲
          </button>
          <button
            type="button"
            aria-label={`Move ${goal.title} down`}
            disabled={index === count - 1}
            onClick={() => onReorder(index, index + 1)}
            className="flex min-h-tap min-w-tap items-center justify-center text-xs text-text-faint hover:text-accent disabled:opacity-20"
          >
            ▼
          </button>
        </div>

        <button
          type="button"
          role="switch"
          aria-checked={goal.done}
          aria-label={`Mark ${goal.title} done`}
          disabled={toggleDone.isPending}
          onClick={() => toggleDone.mutate({ goalId: goal.id, done: !goal.done })}
          className="flex min-h-tap min-w-tap shrink-0 items-center justify-center disabled:opacity-50"
        >
          <span
            aria-hidden="true"
            className={[
              'stamp-visual flex size-6 items-center justify-center rounded-md border-2 text-xs font-bold',
              goal.done ? 'border-accent bg-accent text-accent-contrast' : 'border-border-strong text-transparent',
            ].join(' ')}
          >
            ✓
          </span>
        </button>

        <div className="min-w-0 flex-1">
          <p className={['truncate text-sm font-medium', goal.done ? 'text-text-faint line-through' : 'text-text'].join(' ')}>
            {goal.title}
          </p>
          <p className="truncate text-xs text-text-faint">{goal.type === 'ITEM' ? 'Item' : 'Free-text'}</p>
        </div>

        <button
          type="button"
          onClick={() => setConfirmingDelete(true)}
          aria-label={`Delete goal ${goal.title}`}
          className="flex min-h-tap min-w-tap shrink-0 items-center justify-center rounded-lg text-text-faint hover:bg-danger/15 hover:text-danger"
        >
          <IconTrash />
        </button>
      </li>
      {confirmingDelete && (
        <ConfirmDialog
          title="Delete goal"
          message={`Delete "${goal.title}"? This cannot be undone.`}
          confirmLabel="Delete"
          danger
          pending={deleteGoal.isPending}
          onCancel={() => setConfirmingDelete(false)}
          onConfirm={() => deleteGoal.mutate(goal.id, { onSuccess: () => setConfirmingDelete(false) })}
        />
      )}
    </>
  );
}

export function GoalsSection({ characterId }: { characterId: number }) {
  const goalsQuery = useCharacterGoals(characterId);
  const reorder = useReorderGoals(characterId);
  const [adding, setAdding] = useState(false);

  if (goalsQuery.isPending) {
    return (
      <CollapsibleSection title="Goals">
        <p className="text-sm text-text-muted">Loading goals…</p>
      </CollapsibleSection>
    );
  }
  if (goalsQuery.isError) {
    return (
      <CollapsibleSection title="Goals">
        <p role="alert" className="text-sm text-danger">
          Could not load goals.
        </p>
      </CollapsibleSection>
    );
  }
  const goals = goalsQuery.data ?? [];

  const handleReorder = (from: number, to: number) => {
    if (to < 0 || to >= goals.length) {
      return;
    }
    const ids = goals.map((g) => g.id);
    const reordered = [...ids];
    const [moved] = reordered.splice(from, 1);
    if (moved === undefined) {
      return;
    }
    reordered.splice(to, 0, moved);
    reorder.mutate(reordered);
  };

  return (
    <CollapsibleSection title="Goals" subtitle="Item checklists and free-text goals">
      {goals.length === 0 ? (
        <p className="text-sm text-text-muted">No goals yet.</p>
      ) : (
        <ul className="space-y-2">
          {goals.map((goal, index) => (
            <GoalRow
              key={goal.id}
              characterId={characterId}
              goal={goal}
              index={index}
              count={goals.length}
              onReorder={handleReorder}
            />
          ))}
        </ul>
      )}
      <button
        type="button"
        onClick={() => setAdding(true)}
        className="min-h-tap w-full rounded-lg border border-dashed border-border-strong text-sm font-semibold text-text-faint transition-colors hover:border-accent hover:text-accent"
      >
        + Add goal
      </button>
      {adding && <CreateGoalModal characterId={characterId} onClose={() => setAdding(false)} />}
    </CollapsibleSection>
  );
}
